import { useState, useCallback } from 'react';
import { ITableProps } from '../types/table';
//

// ----------------------------------------------------------------------

export type IUseTable = ITableProps;

export type UseTableProps = {
  defaultDense?: boolean;
  defaultDirection?: 'asc' | 'desc';
  defaultSortBy?: string;
  defaultSelected?: string[];
  defaultPageSize?: number;
  defaultCurrentPage?: number;
};

export default function useTable(props?: UseTableProps): IUseTable {
  const [dense, setDense] = useState(!!props?.defaultDense);

  const [page, setPage] = useState(props?.defaultCurrentPage || 0);

  const [sortBy, setSortBy] = useState(props?.defaultSortBy || 'name');

  const [pageSize, setPageSize] = useState(props?.defaultPageSize || 5);

  const [direction, setDirection] = useState<'asc' | 'desc'>(props?.defaultDirection || 'asc');

  const [selected, setSelected] = useState<string[]>(props?.defaultSelected || []);

  const onSort = useCallback(
    (direction: 'asc' | 'desc', sortBy: string) => {      
        setDirection(direction);
        setSortBy(sortBy);      
    },
    [direction, sortBy]
  );

  const onSelectRow = useCallback(
    (inputValue: string) => {
      const newSelected = selected.includes(inputValue)
        ? selected.filter((value) => value !== inputValue)
        : [...selected, inputValue];

      setSelected(newSelected);
    },
    [selected]
  );

  const onChangePageSize = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setPageSize(parseInt(event.target.value, 10));
  }, []);

  const onChangeDense = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  }, []);

  const onSelectAllRows = useCallback((checked: boolean, inputValue: string[]) => {
    if (checked) {
      setSelected(inputValue);
      return;
    }
    setSelected([]);
  }, []);

  const onChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const onResetPage = useCallback(() => {
    setPage(0);
  }, []);

  const onUpdatePageDeleteRow = useCallback(
    (totalRowsInPage: number) => {
      setSelected([]);
      if (page) {
        if (totalRowsInPage < 2) {
          setPage(page - 1);
        }
      }
    },
    [page]
  );

  const onUpdatePageDeleteRows = useCallback(
    ({
      totalRows,
      totalRowsInPage,
      totalRowsFiltered,
    }: {
      totalRows: number;
      totalRowsInPage: number;
      totalRowsFiltered: number;
    }) => {
      const totalSelected = selected.length;

      setSelected([]);

      if (page) {
        if (totalSelected === totalRowsInPage) {
          setPage(page - 1);
        } else if (totalSelected === totalRowsFiltered) {
          setPage(0);
        } else if (totalSelected > totalRowsInPage) {
          const newPage = Math.ceil((totalRows - totalSelected) / pageSize) - 1;
          setPage(newPage);
        }
      }
    },
    [page, pageSize, selected.length]
  );

  return {
    dense,
    direction,
    page,
    sortBy,
    pageSize,
    //
    selected,
    onSelectRow,
    onSelectAllRows,
    //
    onSort,
    onChangePage,
    onChangeDense,
    onResetPage,
    onChangePageSize,
    onUpdatePageDeleteRow,
    onUpdatePageDeleteRows,
    //
    setPage,
    setDense,
    setDirection,
    setSortBy,
    setSelected,
    setPageSize,
  };
}
